'use client';

import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CHUNK_SIZE_L, perGamesGridChunk } from '../helpers';
import useSearchCorrection from '../hooks/useSearchCorrection';
import { gameSizesConfig, getGridOptions } from './GameGridBlock';
import { SearchGameCategories } from './SearchGameCategories';
import { AnalyticsEventName } from '@/api/analytics';
import { GamesFilter, GameV1 } from '@/api/games';
import { GamesGridStatic } from '@/components/GamesGrid';
import { SectionTitle } from '@/components/SectionTitle';
import { RestrictedProviders } from '@/helpers/conf';
import { mapGameV2ToGameV1 } from '@/helpers/game';
import { GAMES_BY_POPULARITY } from '@/helpers/games';
import { useAnalytics } from '@/hooks/analytics';
import {
  sendSearchResultAppearanceEvent,
  sendSearchResultGameOpenEvent,
} from '@/modules/googleAnalytics/events/searchGamesEvents';
import { useServer } from '@/modules/server/context/ServerEnvContext';
import { useCommonStore } from '@/store';

import { ScResult } from '../styled';

interface IResultWithSimilarProps {
  searchPhrase: string;
}

export const ResultWithSimilar: FC<IResultWithSimilarProps> = ({
  searchPhrase,
}) => {
  const { t } = useTranslation('common');

  const { sendEventToAnalytics } = useAnalytics();
  const { isMobileDevice } = useServer();

  const isLiveSpinsAvailable = useCommonStore.use.isLiveSpinsAvailable();
  const fetchGames = useCommonStore.use.fetchGames();

  const [currentPage, setPage] = useState(1);
  const [pending, setPending] = useState(false);
  const [games, setGames] = useState<GameV1[]>([]);
  const [data, setData] = useState<GamesFilter.Response | null>(null);

  const { correctedQuery: correctedSearchPhrase, correctedMessage } =
    useSearchCorrection(searchPhrase);

  const analyticsSearchText = useMemo(
    () => ({
      search_text: searchPhrase,
      search_text_sent: correctedSearchPhrase,
    }),
    [searchPhrase, correctedSearchPhrase]
  );

  useEffect(() => {
    if (!correctedSearchPhrase) return;
    setPending(true);

    (async () => {
      const data = await fetchGames(
        {
          page: currentPage,
          pageSize: CHUNK_SIZE_L * 2,
          filter: {
            title: correctedSearchPhrase,
          },
          sort: GAMES_BY_POPULARITY,
        },
        (error) => {
          sendSearchResultAppearanceEvent({
            ...analyticsSearchText,
            status: 'error',
            error_text: JSON.stringify(error),
          });
        }
      );
      if (!data) return;

      setData(data);

      if (data.data.length) {
        sendSearchResultAppearanceEvent({
          ...analyticsSearchText,
        });
      } else {
        sendSearchResultAppearanceEvent({
          ...analyticsSearchText,
          status: 'empty_results',
        });
      }
    })();
  }, [
    correctedSearchPhrase,
    searchPhrase,
    currentPage,
    fetchGames,
    analyticsSearchText,
  ]);

  useEffect(() => {
    if (!data) return;
    const mappedGames = mapGameV2ToGameV1(data.data);
    const filteredGames = mappedGames.filter(({ provider }) => {
      const isLiveSpinNotIncluded = !provider.includes(
        RestrictedProviders.LIVESPIN
      );
      const isAutopayNotIncluded = !provider.includes(
        RestrictedProviders.AUTOPAY
      );
      return (
        (isLiveSpinsAvailable || isLiveSpinNotIncluded) && isAutopayNotIncluded
      );
    });
    setGames((games) => [...games, ...filteredGames]);
    setPending(false);
  }, [data, isLiveSpinsAvailable]);

  useEffect(() => {
    setGames([]);
    setPage(1);
  }, [searchPhrase]);

  useEffect(() => {
    if (!searchPhrase) return;
    const name = (() => {
      if (games.length) {
        return AnalyticsEventName.MODAL__SEARCH__RESULT_SUCCESS__APPEARANCE;
      }
      return AnalyticsEventName.MODAL__SEARCH__RESULT_FAILURE__APPEARANCE;
    })();
    sendEventToAnalytics({
      name,
      attributes: {
        additionalData: JSON.stringify({ search_phrase: searchPhrase }),
      },
    });
  }, [games, searchPhrase, sendEventToAnalytics]);

  const sendSearchResultToGA = (game: GameV1) => {
    sendSearchResultGameOpenEvent({
      type: 'search_results',
      provider: game.provider,
      game_name: game.title,
    });
  };

  return (
    <>
      <ScResult>
        {correctedMessage && <p>{correctedMessage}</p>}
        {searchPhrase && <SectionTitle>{t('searchResults')}</SectionTitle>}
        {pending && !games.length ? (
          <p>{t('loadingEllipsis')}</p>
        ) : searchPhrase && !games.length ? (
          <p>{t('game.likeThis')} 👇</p>
        ) : (
          <GamesGridStatic
            perChunkObj={perGamesGridChunk(data?.pagination?.totalCount || 0)}
            games={games}
            loading={false}
            gridOptions={getGridOptions(isMobileDevice)}
            plugGray={isMobileDevice}
            gameSizesConfig={gameSizesConfig}
            totalCount={data?.pagination?.totalCount}
            isSearchModal
            onShowMoreButtonClick={() =>
              data?.pagination?.nextPage && setPage(data.pagination.nextPage)
            }
            onPlayClick={(game) => sendSearchResultToGA(game)}
            onDemoClick={(game) => sendSearchResultToGA(game)}
          />
        )}
      </ScResult>
      {(!searchPhrase || !games.length) && <SearchGameCategories />}
    </>
  );
};
